/**
 * Skeleton. Screen spec 4.10.
 *
 * Shape matched blocks, never a spinner in a layout position. The rule this
 * component exists to enforce is the same one the async narration enforces one
 * level up: if we do not know what step we are on, we say what we are waiting
 * for. A skeleton says it with shape, and it says it in words too, through a
 * polite live region, because shape alone is not available to a screen reader.
 *
 * The shapes are deliberately few. A skeleton that models every detail of the
 * content underneath it is a second implementation of that content, and it
 * goes stale the first time the real thing changes. Five shapes cover the
 * product: a ledger row, a panel, a line of text, a usage track, and a field.
 *
 * `field` is the copy-field slab, and it earns its place the way `track` does:
 * it stands in for a value that arrives late in a place where a line of text
 * would be the wrong height and the wrong width. A DNS record whose value
 * Cloudflare has not minted yet is a slab-shaped hole, not a sentence.
 *
 * The track shape is the overview's zone 5, and it is the reason the shape has
 * to match to the pixel: that zone resolves after every other zone on the
 * page, so a track that is the wrong height would shove the activity list down
 * at the exact moment a person started reading it.
 */

import '../../styles/components/skeleton.css';

export type SkeletonShape = 'row' | 'panel' | 'text' | 'track' | 'field';

export interface SkeletonProps {
  shape?: SkeletonShape;
  /** How many blocks. A list gets its real length, not a token three. */
  count?: number;
  /** What is being waited for, in words: "Loading your deploys". */
  label: string;
}

export function Skeleton({ shape = 'text', count = 1, label }: SkeletonProps) {
  return (
    <div className="skel" role="status" aria-live="polite" aria-busy="true">
      <span className="skel__say">{label}</span>
      {Array.from({ length: count }, (_, index) => (
        <span className="skel__block" data-shape={shape} key={index} aria-hidden="true" />
      ))}
    </div>
  );
}
